import { SPECIMEN_BAG_ID_PREFIX, SPECIMEN_BAG_SKU_ID, SupplyItemStatus, OrderStatus, ProcedureDocumentType } from '@caresend/types';
import { generateID, orderDataToBookings, bookingsDataToProcedures, hasStatus, getProcedureDocumentsByType } from '@caresend/utils';
import update from 'immutability-helper';
import cloneDeep from 'lodash.clonedeep';
import { computed } from 'vue';
import 'firebase/auth';
import { completeInOfficeChartingRequest, getOrderDataRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastSuccess, toastErrorAndReport, toastInfo, toastError } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { getRoute } from '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { keepParams, keepQuery } from '../../../router/helpers/builders.js';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import '../../../router/routes/dashboard/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/visitsPage/routes.js';
import { getChartingFlowHelpers } from './chartingFlowSteps/steps.js';
import { getValidatedCompleteInOfficeChartingRequest, navToExitRoute } from './helpers.js';
import { RequisitionFormLoadingState } from './model.js';
import { initExtendedModule } from '../helpers.js';

const initialState = {
    flowDataIsReady: false,
    flowLoadingCompleted: false,
    nextButtonLoading: false,
    specimenBags: {},
    loadingRequisitionForms: {},
    bookings: undefined,
    procedures: undefined,
};
const chartingFlowState = {
    data: {},
};
const chartingFlowMutations = {
    'chartingFlow/INIT_ORDER': (state, { orderID }) => {
        state.data = update(state.data, {
            [orderID]: { $set: initialState },
        });
    },
    /**
     * Specimen bags are initialized with random IDs if serial is not yet available.
     * They are corrected to match the scanned/entered value when completing the
     * flow.
     */
    'chartingFlow/INIT_SPECIMEN_BAG': (state, { orderID, procedureID, patientID, serial, taskID, }) => {
        var _a;
        const specimenBagID = serial
            ? `${SPECIMEN_BAG_ID_PREFIX}-${serial}`
            : generateID();
        const specimenBag = {
            id: specimenBagID,
            serial: serial !== null && serial !== void 0 ? serial : '',
            skuID: SPECIMEN_BAG_SKU_ID,
            // Marked as `used`, locally, although the supply item will only be
            // marked as used in the DB when charting is completed on a full order.
            status: SupplyItemStatus.USED,
            ...(procedureID && { procedureID }),
            ...(patientID && { patientID }),
            ...(taskID && { taskID }),
        };
        state.data = {
            ...state.data,
            [orderID]: {
                ...state.data[orderID],
                specimenBags: {
                    ...(_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags,
                    [specimenBagID]: specimenBag,
                },
            },
        };
    },
    'chartingFlow/REMOVE_SPECIMEN_BAG': (state, { orderID, specimenBagID }) => {
        state.data = update(state.data, {
            [orderID]: { specimenBags: { $unset: [specimenBagID] } },
        });
    },
    'chartingFlow/REMOVE_TASK_ID_ON_SPECIMEN_BAG': (state, { orderID, specimenBagID, }) => {
        state.data = update(state.data, {
            [orderID]: { specimenBags: {
                    [specimenBagID]: { $unset: ['taskID'] },
                } },
        });
    },
    /**
     * Specimen bags are initialized with random IDs if serial is not yet available.
     * They are corrected to match the scanned/entered value when completing the
     * flow.
     */
    'chartingFlow/REPLACE_ID_ON_SPECIMEN_BAG': (state, { orderID, currentSpecimenBagID, actualSpecimenBagID, }) => {
        var _a, _b;
        const oldSpecimenBag = (_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags) === null || _b === void 0 ? void 0 : _b[currentSpecimenBagID];
        if (!oldSpecimenBag) {
            throw Error(`Missing specimen bag ${currentSpecimenBagID} on order ${orderID}`);
        }
        const specimenBag = {
            ...oldSpecimenBag,
            id: actualSpecimenBagID,
        };
        state.data = update(state.data, {
            [orderID]: {
                specimenBags: {
                    $unset: [currentSpecimenBagID],
                    [actualSpecimenBagID]: { $set: specimenBag },
                },
            },
        });
    },
    'chartingFlow/RESET_SPECIMEN_BAGS': (state, { orderID }) => {
        state.data = update(state.data, {
            [orderID]: { specimenBags: { $set: {} } },
        });
    },
    'chartingFlow/RESET_STATE': (state) => {
        state.data = {};
    },
    'chartingFlow/SET_BOOKINGS': (state, { orderID, bookings }) => {
        state.data = update(state.data, {
            [orderID]: { bookings: { $set: bookings } },
        });
    },
    'chartingFlow/SET_FLOW_DATA_IS_READY': (state, { orderID, ready }) => {
        state.data = update(state.data, {
            [orderID]: { flowDataIsReady: { $set: ready } },
        });
    },
    'chartingFlow/SET_FLOW_LOADING_COMPLETED': (state, { orderID, completed }) => {
        state.data = update(state.data, {
            [orderID]: {
                flowDataIsReady: { $set: completed },
                flowLoadingCompleted: { $set: completed },
            },
        });
    },
    'chartingFlow/SET_FLOW_LOADING_ERROR': (state, { orderID, message }) => {
        state.data = update(state.data, {
            [orderID]: { flowLoadingError: { $set: message } },
        });
    },
    'chartingFlow/SET_NEXT_BUTTON_LOADING': (state, { orderID, isLoading }) => {
        state.data = update(state.data, {
            [orderID]: { nextButtonLoading: { $set: isLoading } },
        });
    },
    'chartingFlow/SET_PROCEDURES': (state, { orderID, procedures }) => {
        state.data = update(state.data, {
            [orderID]: { procedures: { $set: procedures } },
        });
    },
    'chartingFlow/SET_PROCEDURE': (state, { orderID, procedure }) => {
        state.data = update(state.data, {
            [orderID]: { procedures: {
                    [procedure.id]: { $set: procedure },
                } },
        });
    },
    'chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING': (state, { orderID, procedureID, loading, }) => {
        state.data = update(state.data, {
            [orderID]: { loadingRequisitionForms: {
                    [procedureID]: { $set: loading },
                } },
        });
    },
    'chartingFlow/SET_SPECIMEN_BAGS_ON_DRAFT_BOOKING': (state, { orderID, draftBookingID, specimenBagsByTaskID, }) => {
        state.data = update(state.data, {
            [orderID]: { bookings: {
                    [draftBookingID]: {
                        specimenBags: { $set: specimenBagsByTaskID },
                    },
                } },
        });
    },
    'chartingFlow/SET_TASK_ID_ON_SPECIMEN_BAG': (state, { specimenBagID, orderID, taskID, }) => {
        state.data = update(state.data, {
            [orderID]: { specimenBags: {
                    [specimenBagID]: { taskID: { $set: taskID } },
                } },
        });
    },
    'chartingFlow/UPDATE_SERIAL_ON_SPECIMEN_BAG': (state, { orderID, specimenBagID, serial, }) => {
        state.data = update(state.data, {
            [orderID]: { specimenBags: {
                    [specimenBagID]: { serial: { $set: serial } },
                } },
        });
    },
};
const chartingFlowActions = {
    'chartingFlow/completeCharting': async ({ getters }, orderID) => {
        try {
            const request = await getValidatedCompleteInOfficeChartingRequest(orderID);
            await completeInOfficeChartingRequest(request);
            const successMessage = getters['chartingFlow/getSuccessMessage'](orderID);
            toastSuccess(successMessage);
            navToExitRoute(orderID, true);
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
    'chartingFlow/initChartingFlowForDraftOrder': async () => {
        toastInfo('Draft Order not supported yet.');
    },
    'chartingFlow/initChartingFlowForOrder': async (context, orderID) => {
        completeInOfficeChartingRequest({ warmUp: true });
        const { commit } = context;
        commit('chartingFlow/INIT_ORDER', { orderID });
        const getOrderDataInclusions = {
            includeBookingsDataInOrderData: true,
            includeProceduresDataInBookingData: true,
        };
        try {
            const orderData = await getOrderDataRequest({ orderID, getOrderDataInclusions });
            if (!orderData) {
                throw new Error('Order not found');
            }
            const { bookings, bookingsData } = orderDataToBookings([orderData]);
            const { procedures } = bookingsDataToProcedures(bookingsData);
            commit('chartingFlow/SET_PROCEDURES', {
                orderID,
                procedures,
            });
            commit('chartingFlow/SET_BOOKINGS', {
                orderID,
                bookings,
            });
            Object.values(procedures).forEach((procedure) => {
                chartingFlowActions['chartingFlow/tempBindProcedureForReqForm'](context, {
                    orderID,
                    procedureID: procedure.id,
                });
            });
            chartingFlowActions['chartingFlow/initSpecimenBags'](context, {
                orderID,
                procedures: procedures,
                bookings: bookings,
            });
            commit('chartingFlow/SET_FLOW_DATA_IS_READY', {
                orderID,
                ready: true,
            });
        }
        catch (error) {
            toastError(error);
        }
    },
    /**
     * Specimen bags are initialized with random IDs if serial is not yet available.
     * They are corrected to match the scanned/entered value when completing the
     * flow.
     *
     * In office orders only support one booking.
     */
    'chartingFlow/initSpecimenBags': ({ state, commit }, { orderID, procedures, bookings }) => {
        var _a, _b, _c, _d;
        commit('chartingFlow/RESET_SPECIMEN_BAGS', { orderID });
        const allProcedures = cloneDeep((_b = procedures !== null && procedures !== void 0 ? procedures : (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.procedures) !== null && _b !== void 0 ? _b : {});
        const allBookings = cloneDeep((_d = bookings !== null && bookings !== void 0 ? bookings : (_c = state.data[orderID]) === null || _c === void 0 ? void 0 : _c.bookings) !== null && _d !== void 0 ? _d : {});
        Object.values(allProcedures).forEach((procedure) => {
            var _a, _b, _c, _d, _e, _f;
            const serial = (_a = procedure.specimenBag) === null || _a === void 0 ? void 0 : _a.id.replace(SPECIMEN_BAG_ID_PREFIX, '');
            const booking = allBookings[(_b = procedure.bookingID) !== null && _b !== void 0 ? _b : ''];
            commit('chartingFlow/INIT_SPECIMEN_BAG', {
                orderID,
                procedureID: procedure.id,
                serial,
                taskID: procedure.taskID,
                patientID: booking === null || booking === void 0 ? void 0 : booking.patientID,
            });
            /**
             * The specimen bag by taskID would only exist at the procedure level
             * if the order is a full order. Deleting the reference to the booking specimen bag
             * ensures that duplicate specimen bags will not be displayed.
             */
            (_e = (_d = allBookings[(_c = procedure.bookingID) !== null && _c !== void 0 ? _c : '']) === null || _d === void 0 ? void 0 : _d.specimenBags) === null || _e === void 0 ? true : delete _e[(_f = procedure.taskID) !== null && _f !== void 0 ? _f : ''];
        });
        Object.values(allBookings).forEach((booking) => {
            var _a;
            Object.entries((_a = booking.specimenBags) !== null && _a !== void 0 ? _a : {}).forEach(([taskID, specimenBag]) => {
                const serial = specimenBag.id.replace(SPECIMEN_BAG_ID_PREFIX, '');
                commit('chartingFlow/INIT_SPECIMEN_BAG', {
                    orderID,
                    patientID: booking.patientID,
                    serial,
                    taskID,
                });
            });
        });
    },
    'chartingFlow/performCompletionAction': async () => {
        const route = getRoute().value;
        await getChartingFlowHelpers(route).performCompletionAction(route);
    },
    /**
     * Temporarily bind the procedure until the requisition form exists
     * TODO: Move req form gen to create order cloud function
     */
    'chartingFlow/tempBindProcedureForReqForm': async ({ commit, state }, { orderID, procedureID }) => {
        var _a, _b;
        const getProcedureReqForm = (procedure) => {
            var _a, _b;
            const reqForm = (_b = (_a = getProcedureDocumentsByType(procedure, ProcedureDocumentType.INSTRUCTIONAL_REQUISITION_FORM)) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.file;
            return reqForm;
        };
        /** Verify the bind is needed */
        let stateProcedure = (_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.procedures) === null || _b === void 0 ? void 0 : _b[procedureID];
        if (getProcedureReqForm(stateProcedure)) {
            commit('chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING', {
                orderID,
                procedureID,
                loading: RequisitionFormLoadingState.LOADED,
            });
            return;
        }
        /** Set state to loading */
        commit('chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING', {
            orderID,
            procedureID,
            loading: RequisitionFormLoadingState.LOADING,
        });
        /** "Bind" procedure */
        const bind = setInterval(async () => {
            try {
                /** Fetch procedure */
                const getOrderDataInclusions = {
                    includeBookingsDataInOrderData: true,
                    includeProceduresDataInBookingData: true,
                };
                const orderData = await getOrderDataRequest({ orderID, getOrderDataInclusions });
                if (!orderData)
                    throw Error('Order not found');
                const { bookingsData } = orderDataToBookings([orderData]);
                const { procedures } = bookingsDataToProcedures(bookingsData);
                const procedure = procedures[procedureID];
                if (!procedure)
                    throw Error('Procedure not found');
                commit('chartingFlow/SET_PROCEDURE', {
                    orderID: procedure.orderID,
                    procedure,
                });
                const reqForm = getProcedureReqForm(procedure);
                /** If requisition form is successfully generated, unbind and set state to loaded */
                if (reqForm) {
                    commit('chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING', {
                        orderID,
                        procedureID,
                        loading: RequisitionFormLoadingState.LOADED,
                    });
                    clearInterval(bind);
                }
            }
            catch (error) {
                console.info(error);
                commit('chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING', {
                    orderID,
                    procedureID,
                    loading: RequisitionFormLoadingState.ERROR,
                });
                clearInterval(bind);
            }
        }, 3000);
        /** If requisition form is not generated after 15 seconds, set state to error */
        setTimeout(() => {
            var _a, _b;
            stateProcedure = (_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.procedures) === null || _b === void 0 ? void 0 : _b[procedureID];
            if (getProcedureReqForm(stateProcedure))
                return;
            commit('chartingFlow/SET_PROCEDURE_REQUISITION_FORM_LOADING', {
                orderID,
                procedureID,
                loading: RequisitionFormLoadingState.ERROR,
            });
        }, 15000);
    },
};
const chartingFlowGetters = {
    'chartingFlow/getChartingFlowHelpers': () => (route) => getChartingFlowHelpers(route),
    'chartingFlow/getBackStepRoute': () => (route) => {
        const back = chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route)
            .getBackOrNextStepRoute(route, 'back');
        if (!back)
            return null;
        return keepParams(keepQuery(back));
    },
    'chartingFlow/getCurrentStep': () => (route) => chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route)
        .getStepFromRoute(route),
    'chartingFlow/getCurrentStepCompletionAction': () => (route) => chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route).getCurrentStepCompletionAction(route),
    'chartingFlow/getCurrentStepTitle': () => (route) => chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route).getCurrentStepTitle(route),
    'chartingFlow/getCurrentStepValidationFailures': () => (route) => chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route).getCurrentStepValidationFailures(route),
    'chartingFlow/getIsCurrentStepValid': () => (route) => computed(() => chartingFlowGetters['chartingFlow/getCurrentStepValidationFailures']()(route) === null),
    'chartingFlow/getNextStepRouteInCharting': () => (route) => {
        const next = chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route)
            .getBackOrNextStepRoute(route, 'next');
        if (!next)
            return null;
        return keepParams(keepQuery(next));
    },
    'chartingFlow/getNextStepRoute': () => (route) => {
        const nextStep = chartingFlowGetters['chartingFlow/getChartingFlowHelpers']()(route)
            .getBackOrNextStepRoute(route, 'next');
        if (!nextStep)
            return null;
        return keepParams(keepQuery(nextStep));
    },
    'chartingFlow/getSpecimenBagSerialBySpecimenBagID': (state) => (orderID, specimenBagID) => {
        var _a, _b, _c, _d;
        return (_d = (_c = (_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags) === null || _b === void 0 ? void 0 : _b[specimenBagID !== null && specimenBagID !== void 0 ? specimenBagID : '']) === null || _c === void 0 ? void 0 : _c.serial) !== null && _d !== void 0 ? _d : '';
    },
    'chartingFlow/getSuccessMessage': (state) => (orderID) => {
        var _a, _b;
        const s = Object.values((_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags) !== null && _b !== void 0 ? _b : {}).length > 1 ? 's' : '';
        return `Specimen bag${s} saved`;
    },
    'chartingFlow/getErrorMessage': (state, orderID) => {
        var _a, _b;
        const s = Object.values((_b = (_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.specimenBags) !== null && _b !== void 0 ? _b : {}).length > 1 ? 's' : '';
        return `There was an error saving the specimen bag${s}`;
    },
    /**
     * TODO: change to use a variable in state that is set
     * when the order is initialized
     */
    'chartingFlow/isDraftOrder': (state) => (orderID) => {
        var _a;
        if (!orderID)
            return false;
        return hasStatus((_a = state.data[orderID]) === null || _a === void 0 ? void 0 : _a.order, OrderStatus.DRAFT);
    },
};
const chartingFlowModule = {
    state: chartingFlowState,
    mutations: chartingFlowMutations,
    actions: chartingFlowActions,
    getters: chartingFlowGetters,
};
const initChartingFlowModule = (moduleExtensions) => initExtendedModule(chartingFlowModule, moduleExtensions);

export { initChartingFlowModule };
